import React, { useEffect, useState } from 'react';
import api from '../../api/client';

/**
 * OPCO de rattachement, et la suggestion qui vient des conventions collectives.
 *
 * **La liste ne montre que les OPCO.** La table des financeurs en porte
 * désormais d'autres — AGEFICE, FAFCEA, VIVEA, FIF PL, France Travail, CNFPT,
 * ANFH — et les proposer ici laisserait rattacher une entreprise à un fonds
 * d'indépendants : un salarié n'y dépose rien.
 *
 * **Et la suggestion vient du serveur.** Elle était recalculée ici, et deux
 * lectures du même rapprochement finissent par ne pas désigner le même OPCO.
 * La règle vit dans `financeurs.suggerer_opco`, avec ses dix cas : elle écarte
 * le code 9999 (« sans convention collective »), elle rend **toutes** les
 * pistes quand l'entreprise relève de plusieurs branches — LTA Sud-Ouest en
 * porte trois —, et elle met devant celle qui partage le plus de conventions.
 */
export default function OpcoSelector({ idccs, value, onChange }) {
  const [opcos, setOpcos] = useState([]);
  const [pistes, setPistes] = useState([]);

  useEffect(() => {
    api.get('/financeurs')
      .then(({ data }) => setOpcos(
        (data.financeurs || []).filter(
          (o) => (o.famille || 'OPCO') === 'OPCO' && o.active !== false,
        ),
      ))
      .catch((e) => console.warn('[silent]', e));
  }, []);

  useEffect(() => {
    if (!idccs?.length) { setPistes([]); return; }
    api.post('/opco-suggere', { liste_idcc: idccs })
      .then(({ data }) => setPistes(data.pistes || []))
      .catch(() => setPistes([]));
  }, [idccs]);

  const selected = opcos.find((o) => o.id === value) || null;
  // On ne propose que ce qui n'est pas déjà posé.
  const aProposer = pistes.filter((p) => p.id !== value);

  return (
    <div className="mt-3" data-testid="company-opco-selector">
      <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">OPCO de rattachement</label>
      <select
        data-testid="company-opco-select"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
      >
        <option value="">— Non rattaché —</option>
        {opcos.map((o) => (
          <option key={o.id} value={o.id}>{o.code} · {o.name}</option>
        ))}
      </select>

      {/* Les pistes, avec la convention qui les justifie : sans elle, on ne
          sait pas pourquoi celui-là est proposé, et on hésite à le prendre. */}
      {aProposer.length > 0 && (
        <div className="mt-2 flex items-center gap-2 flex-wrap"
             data-testid="company-opco-pistes">
          <span className="text-[11px] text-[#8E8FA5]">
            D&rsquo;après {aProposer.length > 1 ? 'les conventions' : 'la convention'} :
          </span>
          {aProposer.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`company-opco-suggest-${p.code}`}
              onClick={() => onChange(p.id)}
              title={`Convention${p.idcc.length > 1 ? 's' : ''} ${p.idcc.join(', ')}`}
              className="h-8 px-2.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12px] font-semibold hover:bg-[#EDE8FF] whitespace-nowrap"
            >
              {p.code} · IDCC {p.idcc.join(', ')}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="mt-1.5 text-[11px] text-[#6B6C85]">
          Mode : <span className="font-semibold text-[#1A1B2E]">{selected.payment_mode === 'subrogation' ? 'Subrogation directe' : 'Remboursement entreprise'}</span>
          {selected.submission_deadline_days ? ` · Dépôt J-${selected.submission_deadline_days}` : ''}
          {selected.payment_delay_days ? ` · Paiement ${selected.payment_delay_days}j` : ''}
        </div>
      )}
    </div>
  );
}
